import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Clock, Sparkles, Star, BookOpen, Shield, Plus, ChevronRight, Grid, Sun as SunIcon, FileText
} from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import { kundliService, KundliProfile } from '@services/kundliService';
import { useAuthStore } from '@store/authStore';
import { useSettingsStore } from '@store/settingsStore';
import { useKundliStore } from '@store/kundliStore';
import toast from 'react-hot-toast';
import { PAGE_SCHEMAS, SEOHead, SEO_META_TAGS, useSEO } from '@/seo';
import { formatKundliDobForApi, getLocalISODate, normalizeKundliDobToISO, parseKundliDob } from '@utils/dateUtils';

// Components
import PublicPageHeader from '@components/common/PublicPageHeader';
import { Sun } from 'lucide-react';
import KundliForm from './components/KundliForm';
import OverviewTab from './components/OverviewTab';
import KundliTab from './components/KundliTab';
import ChartsTab from './components/ChartsTab';
import DashaTab from './components/DashaTab';
import ReportTab from './components/ReportTab';
import PredictionsTab from './components/PredictionsTab';
import SavedKundliList from './components/SavedKundliList';
import { transformKundliData } from './components/utils';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../languages/freeKundli';

const FreeKundli = () => {
    const { lang: urlLang } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuthStore();
    const { contentLanguage, setContentLanguage } = useSettingsStore();
    const {
        saveKundli,
        updateKundliData,
        currentKundliData,
        currentFormData,
        currentSelectedChart,
        currentViewDasha,
        currentSelectedId,
        setCurrentKundliData,
        setCurrentFormData,
        setCurrentSelectedChart,
        setCurrentViewDasha,
        setCurrentSelectedId,
        clearCurrentSession,
    } = useKundliStore();
    const savedListRef = useRef<{ refresh: () => void }>(null);

    // Form State - clean form for new entries
    const getDefaultFormData = () => {
        const now = new Date();
        return {
            name: '',
            dob: getLocalISODate(),
            birth_time: now.toTimeString().split(' ')[0].substring(0, 5),
            location: '',
            latitude: 28.6139,
            longitude: 77.2090,
            timezone: '+5.5',
            gender: 'Male',
            marital_status: 'Single',
            occupation: 'Professional'
        };
    };

    // Initialize form data from store or defaults
    const [formData, setFormDataLocal] = useState(() => currentFormData || getDefaultFormData());
    const [loading, setLoading] = useState(false);

    // Sync form data to store
    const setFormData = (data: any) => {
        setFormDataLocal(data);
        setCurrentFormData(data);
    };

    // Language state - initialized from URL param, Zustand store settings
    const [language, setLanguage] = useState<'english' | 'hindi'>(() => {
        if (urlLang === 'hindi' || urlLang === 'english') {
            return urlLang as 'english' | 'hindi';
        }
        if (user?.content_language) {
            const lang = user.content_language.toLowerCase();
            return lang === 'hindi' ? 'hindi' : 'english';
        }
        return contentLanguage;
    });

    // Listen to Zustand store changes for language
    useEffect(() => {
        let storeLang: 'english' | 'hindi' = contentLanguage;

        if (user?.content_language) {
            const userLang = user.content_language.toLowerCase();
            storeLang = userLang === 'hindi' ? 'hindi' : 'english';
        }

        if (storeLang !== language) {
            setLanguage(storeLang);
        }
    }, [user?.content_language, contentLanguage]);

    // Update state when URL param changes
    useEffect(() => {
        if (urlLang && (urlLang === 'hindi' || urlLang === 'english') && urlLang !== language) {
            setLanguage(urlLang as 'english' | 'hindi');
        }
    }, [urlLang]);

    // Update URL when language changes
    useEffect(() => {
        navigate(`/free-kundli/${language}`, { replace: true });
        setContentLanguage(language);
    }, [language, navigate, setContentLanguage]);

    // Handle language change from footer
    const handleLanguageChange = (lang: string) => {
        const newLang = lang === 'hindi' ? 'hindi' : 'english';
        setLanguage(newLang);
    };

    // Kundli result data from store
    const [kundliData, setKundliDataLocal] = useState<any>(currentKundliData);

    // Sync kundli data to store
    const setKundliData = (data: any) => {
        setKundliDataLocal(data);
        setCurrentKundliData(data);
    };

    // Selected kundli ID from store
    const [selectedKundliId, setSelectedKundliIdLocal] = useState<string | null>(currentSelectedId);

    const setSelectedKundliId = (id: string | null) => {
        setSelectedKundliIdLocal(id);
        setCurrentSelectedId(id);
    };

    const handleClear = () => {
        setFormData(getDefaultFormData());
        toast.success('Form cleared');
    };

    const handleNewKundli = () => {
        // Clear store session
        clearCurrentSession();

        setKundliDataLocal(null);
        setViewDashaLocal([]);
        setActiveTab('overview');
        setSelectedKundliIdLocal(null);
        setFormDataLocal(getDefaultFormData());
        setSelectedChartLocal('D1');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const [activeTab, setActiveTab] = useState('overview');

    // Selected chart from store
    const [selectedChart, setSelectedChartLocal] = useState(currentSelectedChart);

    const setSelectedChart = (chart: string) => {
        setSelectedChartLocal(chart);
        setCurrentSelectedChart(chart);
    };

    // View dasha from store
    const [viewDasha, setViewDashaLocal] = useState<any[]>(currentViewDasha);

    const setViewDasha = (dasha: any[]) => {
        setViewDashaLocal(dasha);
        setCurrentViewDasha(dasha);
    };

    const [refreshKey, setRefreshKey] = useState(0);
    const autoGenerateTriggered = useRef(false);

    // Auto-generate kundli when coming from compatibility page with autoGenerate flag
    useEffect(() => {
        const shouldAutoGenerate = searchParams.get('autoGenerate') === 'true';

        if (shouldAutoGenerate && !autoGenerateTriggered.current && formData.name && formData.dob && formData.location && !kundliData) {
            autoGenerateTriggered.current = true;
            // Remove the query param to prevent re-triggering
            searchParams.delete('autoGenerate');
            setSearchParams(searchParams, { replace: true });

            // Trigger generation after a short delay to ensure state is ready
            setTimeout(() => {
                handleGenerateInternal(true);
            }, 100);
        }
    }, [searchParams, formData, kundliData]);

    // Internal generate function for auto-generate
    const handleGenerateInternal = async (silent: boolean = false) => {
        if (!formData.name || !formData.dob || !formData.location) {
            if (!silent) toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            const parsedDate = parseKundliDob(formData.dob);
            if (!parsedDate) {
                if (!silent) toast.error('Invalid date format');
                setLoading(false);
                return;
            }

            const timeParts = formData.birth_time.split(':');

            const payload = {
                name: formData.name,
                gender: formData.gender,
                year: parsedDate.year,
                month: parsedDate.month,
                day: parsedDate.day,
                hour: parseInt(timeParts[0]),
                minute: parseInt(timeParts[1]),
                second: timeParts[2] ? parseInt(timeParts[2]) : 0,
                location: formData.location,
                latitude: formData.latitude,
                longitude: formData.longitude,
                timezone: formData.timezone || '+5.5',
                language: language
            };

            const response = await kundliService.generateRawData(payload);
            const transformedData = transformKundliData(response.kundli_data);
            setKundliData(transformedData);

            if (!silent) {
                toast.success('Kundli generated successfully!');
            }

            setTimeout(() => {
                document.getElementById('kundli-results')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        } catch (error) {
            console.error('Error generating kundli:', error);
            if (!silent) toast.error('Failed to generate kundli. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (silent?: boolean | React.MouseEvent) => {
        // Check if silent is explicitly true (not an event object from onClick)
        const isSilent = silent === true;

        if (!formData.name || !formData.dob || !formData.location) {
            if (!isSilent) toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            // Parse date through the centralized Kundli DOB helper before calculations.
            const parsedDate = parseKundliDob(formData.dob);
            if (!parsedDate) {
                if (!isSilent) toast.error('Invalid date format');
                setLoading(false);
                return;
            }

            const timeParts = formData.birth_time.split(':');

            const payload = {
                name: formData.name,
                gender: formData.gender,
                year: parsedDate.year,
                month: parsedDate.month,
                day: parsedDate.day,
                hour: parseInt(timeParts[0]),
                minute: parseInt(timeParts[1]),
                second: timeParts[2] ? parseInt(timeParts[2]) : 0,
                location: formData.location,
                latitude: formData.latitude,
                longitude: formData.longitude,
                timezone: formData.timezone || '+5.5',
                language: language
            };

            const response = await kundliService.generateRawData(payload);
            const transformedData = transformKundliData(response.kundli_data);
            setKundliData(transformedData);

            // Auto-save logic
            if (!isSilent) {
                if (isAuthenticated) {
                    // Save to cloud for logged-in users
                    try {
                        const apiDob = formatKundliDobForApi(formData.dob);
                        if (!apiDob) {
                            throw new Error('Invalid date format');
                        }

                        await kundliService.createProfile({
                            profile_name: formData.name,
                            name: formData.name,
                            dob: apiDob,
                            birth_time: formData.birth_time,
                            birth_location: formData.location,
                            latitude: formData.latitude,
                            longitude: formData.longitude,
                            timezone: formData.timezone || '+5.5',
                            gender: formData.gender
                        });
                        toast.success('Kundli generated & saved to cloud!');
                    } catch (saveError: any) {
                        // Profile might already exist
                        if (saveError?.response?.status === 409) {
                            toast.success('Kundli generated successfully!');
                        } else {
                            console.error('Failed to save to cloud:', saveError);
                            toast.success('Kundli generated! (Cloud save failed)');
                        }
                    }
                } else {
                    // Save to localStorage via Zustand for guests
                    const savedKundli = saveKundli({
                        name: formData.name,
                        gender: formData.gender,
                        dob: formData.dob,
                        birth_time: formData.birth_time,
                        location: formData.location,
                        latitude: formData.latitude,
                        longitude: formData.longitude,
                        timezone: formData.timezone,
                        kundli_data: transformedData
                    });
                    setSelectedKundliId(savedKundli.id);
                    toast.success('Kundli generated & saved locally!');
                }

                // Refresh the saved list
                setRefreshKey(prev => prev + 1);

                setTimeout(() => {
                    document.getElementById('kundli-results')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }

        } catch (error) {
            console.error('Error generating kundli:', error);
            if (!isSilent) toast.error('Failed to generate kundli. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle selecting a saved kundli
    const handleSelectKundli = async (kundli: any, isCloud: boolean) => {
        setSelectedKundliId(kundli.id);

        // Normalize DOB to ISO format (YYYY-MM-DD) for the DatePicker.
        const normalizedDob = normalizeKundliDobToISO(kundli.dob);

        // Populate form data
        const newFormData = {
            name: kundli.name,
            gender: kundli.gender,
            dob: normalizedDob,
            birth_time: kundli.birth_time,
            location: kundli.location || kundli.birth_location || '',
            latitude: kundli.latitude,
            longitude: kundli.longitude,
            timezone: kundli.timezone || '+5.5',
            marital_status: kundli.marital_status || 'Single',
            occupation: kundli.occupation || 'Professional'
        };
        setFormData(newFormData);

        // If local kundli has cached data, use it
        if (!isCloud && kundli.kundli_data) {
            setKundliData(kundli.kundli_data);
            toast.success('Kundli generated successfully!');
            setTimeout(() => {
                document.getElementById('kundli-results')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        } else {
            // Generate kundli data
            setLoading(true);
            try {
                // Parse date through the centralized Kundli DOB helper before calculations.
                const parsedDate = parseKundliDob(kundli.dob);
                if (!parsedDate) {
                    toast.error('Invalid date format');
                    setLoading(false);
                    return;
                }

                const timeParts = newFormData.birth_time.split(':');

                const payload = {
                    name: newFormData.name,
                    gender: newFormData.gender,
                    year: parsedDate.year,
                    month: parsedDate.month,
                    day: parsedDate.day,
                    hour: parseInt(timeParts[0]),
                    minute: parseInt(timeParts[1]),
                    second: timeParts[2] ? parseInt(timeParts[2]) : 0,
                    location: newFormData.location,
                    latitude: newFormData.latitude,
                    longitude: newFormData.longitude,
                    timezone: newFormData.timezone || '+5.5',
                    language: language
                };

                const response = await kundliService.generateRawData(payload);
                const transformedData = transformKundliData(response.kundli_data);
                setKundliData(transformedData);

                // Cache the data for local kundlis
                if (!isCloud) {
                    updateKundliData(kundli.id, transformedData);
                }

                toast.success('Kundli generated successfully!');
                setTimeout(() => {
                    document.getElementById('kundli-results')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            } catch (error) {
                console.error('Error loading kundli:', error);
                toast.error('Failed to load kundli data');
            } finally {
                setLoading(false);
            }
        }
    };

    // Track previous language to detect changes
    const prevLanguageRef = useRef(language);

    // Regenerate kundli when language changes
    useEffect(() => {
        // Only regenerate if language actually changed (not on initial mount)
        if (prevLanguageRef.current !== language) {
            prevLanguageRef.current = language;

            // If we have existing kundli data, refetch with new language
            if (kundliData && formData.dob && formData.birth_time && formData.location) {
                const regenerate = async () => {
                    setLoading(true);
                    try {
                        // Parse date through the centralized Kundli DOB helper before calculations.
                        const parsedDate = parseKundliDob(formData.dob);
                        if (!parsedDate) {
                            toast.error('Invalid date format');
                            setLoading(false);
                            return;
                        }

                        const timeParts = formData.birth_time.split(':');

                        const payload = {
                            name: formData.name,
                            gender: formData.gender,
                            year: parsedDate.year,
                            month: parsedDate.month,
                            day: parsedDate.day,
                            hour: parseInt(timeParts[0]),
                            minute: parseInt(timeParts[1]),
                            second: timeParts[2] ? parseInt(timeParts[2]) : 0,
                            location: formData.location,
                            latitude: formData.latitude,
                            longitude: formData.longitude,
                            timezone: formData.timezone || '+5.5',
                            language: language
                        };

                        console.log('Regenerating kundli with language:', language);
                        const response = await kundliService.generateRawData(payload);
                        const transformedData = transformKundliData(response.kundli_data);
                        setKundliData(transformedData);
                        // Reset viewDasha to avoid stale language data in nested dasha views
                        setViewDasha([]);
                        toast.success(language === 'hindi' ? 'भाषा बदली गई' : 'Language changed');
                    } catch (error) {
                        console.error('Error regenerating kundli with new language:', error);
                        toast.error('Failed to update language');
                    } finally {
                        setLoading(false);
                    }
                };
                regenerate();
            }
        }
    }, [language, kundliData, formData]);

    // Language translations
    const languageKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[languageKey];

    const tabs = [
        { id: 'overview', label: t.overview, icon: BookOpen },
        { id: 'kundli', label: t.kundli || 'Kundli', icon: SunIcon },
        { id: 'charts', label: t.charts || 'Charts', icon: Grid },
        { id: 'dasha', label: t.dasha, icon: Clock },
        { id: 'predictions', label: t.predictions, icon: Sparkles },
        { id: 'report', label: t.report || 'Report', icon: FileText }
    ];

    return (
        <div className="min-h-screen bg-cosmic-950 text-white font-sans selection:bg-primary-900 selection:text-white relative pb-16 md:pb-0">
            {/* SEO Meta Tags */}
            <SEOHead
                {...useSEO(
                    SEO_META_TAGS.freeKundli[language === 'hindi' ? 'Hindi' : 'English'],
                    language === 'hindi' ? 'Hindi' : 'English'
                )}
                structuredData={PAGE_SCHEMAS.freeKundli}
            />

            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-12 md:pb-24 px-3 sm:px-6">
                <div className={`container mx-auto ${kundliData ? 'max-w-5xl' : 'max-w-7xl'}`}>

                    <PublicPageHeader
                        title={t.freeKundliOnline}
                        highlightedText={t.freeKundliOnline}
                        description={t.freeKundliDesc}
                        icon={Sun}
                    />

                    {/* Form with Saved Kundli List Grid */}
                    {!kundliData && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Saved Kundli List - Shows at top on mobile */}
                            <div className="lg:col-span-1 lg:order-2">
                                <div className="lg:sticky lg:top-24">
                                    <SavedKundliList
                                        key={refreshKey}
                                        onSelect={handleSelectKundli}
                                        selectedId={selectedKundliId}
                                        language={language}
                                    />
                                </div>
                            </div>
                            {/* Kundli Form - Shows below saved list on mobile */}
                            <div className="lg:col-span-2 lg:order-1">
                                <KundliForm
                                    formData={formData}
                                    setFormData={setFormData}
                                    handleGenerate={handleGenerate}
                                    handleClear={handleClear}
                                    loading={loading}
                                    language={language}
                                />
                            </div>
                        </div>
                    )}

                    {/* Full Width Results */}

                    {kundliData && (
                        <div
                            id="kundli-results"
                            className="space-y-6 md:space-y-12"
                        >
                            {/* Results Tabs */}
                            <div className="pb-4 md:pb-6 border-b border-white/20">
                                {/* Desktop: Tabs on left, New Kundli on right */}
                                <div className="hidden md:flex md:items-center md:justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        {tabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`px-6 py-3 rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap h-[50px] ${activeTab === tab.id
                                                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/40'
                                                    : 'bg-white/5 text-primary-200/60 hover:text-white hover:bg-white/10'
                                                    }`}
                                            >
                                                <tab.icon size={20} />
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleNewKundli}
                                            className="px-6 h-[50px] rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700 shadow-lg shadow-primary-900/40"
                                        >
                                            <Plus size={20} />
                                            {t.newKundli}
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile: New Kundli button above grid */}
                                <div className="md:hidden space-y-3">
                                    <button
                                        onClick={handleNewKundli}
                                        className="w-full px-4 py-3 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700 shadow-lg shadow-primary-900/40"
                                    >
                                        <Plus size={18} />
                                        {t.newKundli}
                                    </button>
                                    <div className="grid grid-cols-3 gap-2">
                                        {tabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`px-2 py-2.5 rounded-xl text-[10px] font-bold transition-colors flex flex-col items-center justify-center gap-1.5 whitespace-nowrap ${activeTab === tab.id
                                                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/40'
                                                    : 'bg-white/5 text-primary-200/60 hover:text-white hover:bg-white/10'
                                                    }`}
                                            >
                                                <tab.icon size={16} />
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Content Logic */}
                            {activeTab === 'overview' && (
                                <OverviewTab
                                    kundliData={kundliData}
                                    formData={formData}
                                    language={language}
                                />
                            )}

                            {activeTab === 'kundli' && <KundliTab kundliData={kundliData} language={language} />}

                            {activeTab === 'charts' && <ChartsTab kundliData={kundliData} language={language} />}

                            {activeTab === 'dasha' && (
                                <DashaTab
                                    kundliData={kundliData}
                                    viewDasha={viewDasha}
                                    setViewDasha={setViewDasha}
                                    language={language}
                                />
                            )}

                            {activeTab === 'predictions' && <PredictionsTab kundliData={kundliData} language={language} />}

                            {activeTab === 'report' && <ReportTab kundliData={kundliData} language={language} />}

                            {/* Call to Action */}
                            <div className="mt-8 p-6 md:p-12 rounded-[32px] md:rounded-[48px] bg-white/[0.08] border border-white/20 text-center relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600/10 blur-[120px] rounded-full -mr-48 -mt-48 transition-colors group-hover:bg-primary-600/20" />
                                <div className="relative z-10">
                                    <h2 className="text-2xl md:text-5xl font-black text-white mb-6">{t.wantGuidance}</h2>
                                    <p className="text-primary-200/60 text-base md:text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                                        {t.guidanceDesc}
                                    </p>
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className="px-6 md:px-10 py-4 md:py-5 bg-white text-primary-900 font-black rounded-2xl hover:scale-105 transition-colors shadow-2xl flex items-center gap-3 mx-auto uppercase tracking-widest text-sm md:text-base"
                                    >
                                        <Sparkles size={20} />
                                        {t.chatWithAI}
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <Footer />
            <MobileBottomNav />
        </div>
    );
};

export default FreeKundli;
